import { apiSlice } from './apiSlice';
import type {
  EffectiveSoilState,
  SoilMeasurement,
  SoilMeasurementPayload,
  SoilState,
  SoilStatePayload,
} from '../types/soil';

const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const adaptMetrics = (metrics: any) => ({
  moisturePct: toNumberOrNull(metrics?.moisturePct ?? metrics?.moisture_pct),
  temperatureC: toNumberOrNull(metrics?.temperatureC ?? metrics?.temperature_c),
  pH: toNumberOrNull(metrics?.pH ?? metrics?.ph),
  nitrogen: toNumberOrNull(metrics?.nitrogen),
  phosphorus: toNumberOrNull(metrics?.phosphorus),
  potassium: toNumberOrNull(metrics?.potassium),
  organicMatter: toNumberOrNull(metrics?.organicMatter ?? metrics?.organic_matter),
  compaction: toNumberOrNull(metrics?.compaction),
  salinity: toNumberOrNull(metrics?.salinity),
});

const adaptSoilState = (item: any): SoilState => ({
  id: item.id,
  farmId: item.farmId ?? item.farm_id,
  fieldId: item.fieldId ?? item.field_id,
  plotId: item.plotId ?? item.plot_id,
  bedId: item.bedId ?? item.bed_id,
  sourceType: item.sourceType ?? item.source_type,
  observedAt: item.observedAt ?? item.observed_at ?? null,
  confidencePct: toNumberOrNull(item.confidencePct ?? item.confidence_pct),
  metrics: adaptMetrics(item.metrics),
  notes: item.notes ?? null,
  metadata: item.metadata ?? {},
  createdBy: item.createdBy ?? item.created_by ?? null,
  updatedBy: item.updatedBy ?? item.updated_by ?? null,
  createdAt: item.createdAt ?? item.created_at,
  updatedAt: item.updatedAt ?? item.updated_at,
});

const adaptMeasurement = (item: any): SoilMeasurement => ({
  id: item.id,
  soilStateId: item.soilStateId ?? item.soil_state_id,
  farmId: item.farmId ?? item.farm_id,
  bedId: item.bedId ?? item.bed_id,
  sourceType: item.sourceType ?? item.source_type,
  measuredAt: item.measuredAt ?? item.measured_at,
  sampleDepthFromCm: toNumberOrNull(item.sampleDepthFromCm ?? item.sample_depth_from_cm),
  sampleDepthToCm: toNumberOrNull(item.sampleDepthToCm ?? item.sample_depth_to_cm),
  metrics: adaptMetrics(item.metrics),
  notes: item.notes ?? null,
  metadata: item.metadata ?? {},
  createdBy: item.createdBy ?? item.created_by ?? null,
  createdAt: item.createdAt ?? item.created_at,
});

export const soilStateApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getSoilStateByBed: build.query<SoilState, string>({
      query: (bedId) => ({ url: `/soil-states/bed/${bedId}` }),
      transformResponse: (response: any) => adaptSoilState(response),
      providesTags: (result, error, bedId) => [
        { type: 'Bed' as const, id: `${bedId}-soil-state` },
        { type: 'Bed' as const, id: `${bedId}-soil-measurements` },
      ],
    }),
    getEffectiveSoilStateByBed: build.query<EffectiveSoilState, { bedId: string; date?: string | null }>({
      query: ({ bedId, date }) => ({
        url: `/soil-states/bed/${bedId}/effective`,
        params: date ? { date } : undefined,
      }),
      transformResponse: (response: any) => ({
        bedId: response?.bedId ?? response?.bed_id,
        resolvedAt: response?.resolvedAt ?? response?.resolved_at,
        state: response?.state ? adaptSoilState(response.state) : null,
      }),
      providesTags: (result, error, { bedId }) => [{ type: 'Bed' as const, id: `${bedId}-soil-state` }],
    }),
    getSoilMeasurementsByBed: build.query<SoilMeasurement[], { bedId: string; limit?: number }>({
      query: ({ bedId, limit }) => ({
        url: `/soil-states/bed/${bedId}/measurements`,
        params: limit ? { limit } : undefined,
      }),
      transformResponse: (response: any) => Array.isArray(response) ? response.map(adaptMeasurement) : [],
      providesTags: (result, error, { bedId }) => [{ type: 'Bed' as const, id: `${bedId}-soil-measurements` }],
    }),
    upsertSoilStateByBed: build.mutation<SoilState, { bedId: string; payload: SoilStatePayload }>({
      query: ({ bedId, payload }) => ({
        url: `/soil-states/bed/${bedId}`,
        method: 'PUT',
        body: payload,
      }),
      transformResponse: (response: any) => adaptSoilState(response),
      invalidatesTags: (result, error, { bedId }) => [
        { type: 'Bed' as const, id: `${bedId}-soil-state` },
        { type: 'Bed' as const, id: `${bedId}-soil-measurements` },
      ],
    }),
    createSoilMeasurementByBed: build.mutation<SoilMeasurement, { bedId: string; payload: SoilMeasurementPayload }>({
      query: ({ bedId, payload }) => ({
        url: `/soil-states/bed/${bedId}/measurements`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (response: any) => adaptMeasurement(response),
      invalidatesTags: (result, error, { bedId }) => [
        { type: 'Bed' as const, id: `${bedId}-soil-state` },
        { type: 'Bed' as const, id: `${bedId}-soil-measurements` },
      ],
    }),
  }),
});

export const {
  useGetSoilStateByBedQuery,
  useGetEffectiveSoilStateByBedQuery,
  useGetSoilMeasurementsByBedQuery,
  useUpsertSoilStateByBedMutation,
  useCreateSoilMeasurementByBedMutation,
} = soilStateApi;